/**
 * Capacitor / Mobile platform utilities.
 * Safe to import on web — all Capacitor APIs are no-ops when running in a browser.
 */

import { TOKEN_KEY } from "../api/client";
import brand from "../config/brand";

/** True when running inside a Capacitor native shell (Android / iOS). */
export const isNative = () => {
  return (
    typeof window !== "undefined" &&
    window.Capacitor !== undefined &&
    window.Capacitor.isNativePlatform()
  );
};

/** Returns "android" | "ios" | "web" */
export const getPlatform = () => {
  if (typeof window === "undefined") return "web";
  if (window.Capacitor?.getPlatform) return window.Capacitor.getPlatform();
  return "web";
};

export const isAndroid = () => getPlatform() === "android";
export const isIOS = () => getPlatform() === "ios";
export const isWeb = () => getPlatform() === "web";

/**
 * Mark the document when running inside the Capacitor native shell.
 */
export function markNativeDocument() {
  if (typeof document === "undefined" || !isNative()) return;
  document.documentElement.classList.add("is-native");
}

function isEditableField(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(el.isContentEditable);
}

const PICKER_INPUT_TYPES = new Set([
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
  "file",
  "color",
]);

/** Native Android pickers fire a hardware-back event when they close. */
const PICKER_BACK_GRACE_MS = 1000;
let lastPickerDismissAt = 0;

function isNativePickerField(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.tagName === "SELECT") return true;
  if (el.tagName === "INPUT") {
    const type = String(el.getAttribute("type") || el.type || "").toLowerCase();
    return PICKER_INPUT_TYPES.has(type);
  }
  return false;
}

export function resetNativePickerBackGuard() {
  lastPickerDismissAt = 0;
}

/**
 * True when hardware back should stay on the current page: keyboard open,
 * native select/date/time/file picker open, or that picker just closed.
 * Blurs the field so a second back can still leave the page.
 */
export function consumeHardwareBackForFocusedField() {
  if (typeof document === "undefined") return false;
  const active = document.activeElement;
  if (isNativePickerField(active)) {
    if (typeof active.blur === "function") active.blur();
    lastPickerDismissAt = Date.now();
    return true;
  }
  if (isEditableField(active)) {
    if (typeof active.blur === "function") active.blur();
    return true;
  }
  return Date.now() - lastPickerDismissAt < PICKER_BACK_GRACE_MS;
}

/**
 * Remember when a native picker loses focus so the back event that closes
 * it is not treated as "leave this screen".
 */
export function installNativePickerBackGuard() {
  if (typeof document === "undefined") return () => {};

  const onFocusOut = (event) => {
    if (isNativePickerField(event.target)) {
      lastPickerDismissAt = Date.now();
    }
  };

  document.addEventListener("focusout", onFocusOut, true);
  return () => document.removeEventListener("focusout", onFocusOut, true);
}

/**
 * Android IMEs (Gboard Arabic especially) keep the last word in composition.
 * Tapping Send/Submit can read React state before that word is committed.
 * Blur the focused field first so `onChange` runs before the click handler.
 */
export function commitFocusedIme(eventTarget) {
  if (typeof document === "undefined") return;
  const active = document.activeElement;
  if (!isEditableField(active)) return;
  if (eventTarget && (eventTarget === active || active.contains(eventTarget))) {
    return;
  }
  if (
    eventTarget &&
    typeof eventTarget.closest === "function" &&
    eventTarget.closest("input, textarea, select, [contenteditable='true']")
  ) {
    return;
  }
  active.blur();
}

/**
 * Install a capture-phase listener that commits IME composition before
 * button/link taps. Safe to call more than once; returns an uninstall fn.
 */
export function installNativeImeCommit() {
  if (typeof document === "undefined") return () => {};

  const onPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    commitFocusedIme(target);
  };

  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("touchstart", onPointerDown, true);
  return () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("touchstart", onPointerDown, true);
  };
}

/**
 * Initialise Capacitor plugins that should run at app startup.
 * Called once from index.js after React mounts.
 */
export async function initCapacitor() {
  markNativeDocument();
  if (!isNative()) return;

  installNativeImeCommit();
  installNativePickerBackGuard();

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch {
    // SplashScreen plugin not available — safe to ignore
  }

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: brand.primaryColor });
    await StatusBar.show();
  } catch {
    // StatusBar plugin not available — safe to ignore
  }
}

/**
 * Register Capacitor App event listeners (back button, deep links, etc.).
 * Returns a cleanup function to remove listeners.
 */
export async function registerAppListeners({ onDeepLink } = {}) {
  if (!isNative()) return () => {};

  const listeners = [];

  try {
    const { App } = await import("@capacitor/app");

    // Android hardware back — never treat as logout; only Logout button clears session
    const backListener = await App.addListener("backButton", ({ canGoBack }) => {
      // Select/date/time pickers close with a back event; stay on the form.
      if (consumeHardwareBackForFocusedField()) return;

      const backEvent = new CustomEvent("app:backbutton", { cancelable: true });
      window.dispatchEvent(backEvent);
      if (backEvent.defaultPrevented) return;

      const isAuthed = Boolean(localStorage.getItem(TOKEN_KEY));
      const path = window.location.pathname || "/";
      const onAuthShell =
        isAuthed && path !== "/login" && !path.startsWith("/login");

      // Stay in the authenticated app — do not history.back() onto /login
      if (onAuthShell) {
        return;
      }

      if (!canGoBack) {
        return;
      }
      window.history.back();
    });
    listeners.push(backListener);

    // Deep link / universal link handling
    if (typeof onDeepLink === "function") {
      const deepLinkListener = await App.addListener("appUrlOpen", (data) => {
        onDeepLink(data.url);
      });
      listeners.push(deepLinkListener);
    }

    // Resume is registered in AppShell so force-update can recheck even
    // when BrowserRouter / DeepLinkHandler are not mounted.
  } catch {
    // App plugin not available
  }

  return () => {
    listeners.forEach((l) => l?.remove?.());
  };
}

/**
 * Native version info from Capacitor App plugin.
 * `build` is Android versionCode (string); `version` is versionName.
 */
function bakedVersionInfo() {
  return {
    version: process.env.REACT_APP_VERSION || "1.0.0",
    build: String(process.env.REACT_APP_VERSION_CODE || "0"),
  };
}

function resolveBuildNumber(rawBuild, fallbackBuild) {
  const native = parseInt(String(rawBuild ?? ""), 10);
  if (Number.isFinite(native) && native > 0) return String(native);
  const baked = parseInt(String(fallbackBuild ?? ""), 10);
  if (Number.isFinite(baked) && baked > 0) return String(baked);
  return "0";
}

export async function getNativeVersionInfo() {
  const baked = bakedVersionInfo();
  if (!isNative()) {
    return baked;
  }

  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return {
      version: info?.version || baked.version,
      build: resolveBuildNumber(info?.build, baked.build),
    };
  } catch {
    return baked;
  }
}

/** Returns the app version string from Capacitor App plugin, or package version. */
export async function getAppVersion() {
  const info = await getNativeVersionInfo();
  if (isNative()) {
    return `${info.version} (${info.build})`;
  }
  return info.version;
}
